import Button from "@components/common/Button";
import ImageRenderer from "@components/common/Image";
import Link from "@components/common/Link";
import Title from "@components/common/Title";
import { useWrapper } from "@context/wrapper";
import {
  ImagePosition,
  ImageWrapperDynamicZoneModel,
  ImageWrapperVariants,
} from "@models/strapi-components/image-wrapper";
import React from "react";
import { twMerge } from "tailwind-merge";

export interface ImageWrapperProps {
  componentData: ImageWrapperDynamicZoneModel;
  offsetClass?: string;
}
const ImageWrapper: React.FunctionComponent<ImageWrapperProps> = ({
  componentData,
  offsetClass,
}) => {
  const {
    imageWrapper: image,
    title,
    subTitle,
    variant = ImageWrapperVariants.VERTICAL,
    imgPosition = ImagePosition.RIGHT,
    divColor,
    learnMoreText,
    learnMoreLink,
  } = componentData;

  const variantNameClassName = {
    [ImageWrapperVariants.VERTICAL]: {
      wrapperClassName: "",
      titleClassName: "text-center md:max-w-[60%] xl:max-w-[60%]",
      subTitleClassName: "text-center md:w-1/2 xl:w-1/3",
      textWrapperClassName: "items-center",
    },
    [ImageWrapperVariants.HORIZONTAL]: {
      wrapperClassName:
        imgPosition === ImagePosition.RIGHT
          ? "lg:flex items-center xl:gap-[100px] md:px-12 xl:px-[140px] justify-between"
          : "lg:flex flex-row-reverse items-center xl:gap-[100px] md:px-12 xl:px-[140px] justify-between",
      titleClassName: "lg:text-left",
      subTitleClassName:
        "text-center lg:text-left md:max-w-[70%] lg:max-w-none",
      textWrapperClassName:
        "lg:max-w-[300px] xl:max-w-[428px] items-center lg:items-start",
    },
  };

  const {
    wrapperClassName,
    titleClassName,
    subTitleClassName,
    textWrapperClassName,
  } = variantNameClassName[variant] ?? {};

  // I am sorry for this :( but I am running out of time and patience
  // They have lot of adhoc requests
  const color = divColor === "#050505" ? "#ffffff" : "#291E30";

  const isCtaLinkToTop = learnMoreLink === "#";

  const { wrapperRef } = useWrapper();

  const isBothCtaLinkAndTextAbsent = !learnMoreLink && !learnMoreText;

  const ctaComponent = (function () {
    if (isBothCtaLinkAndTextAbsent) return null;

    if (isCtaLinkToTop) {
      return (
        <Button
          className="mt-8 xl:mt-12"
          variant="primaryReversed"
          style={{ padding: 0, minHeight: "43px" }}
          onClick={() => {
            if (!isCtaLinkToTop) {
              return;
            }

            wrapperRef?.current?.scroll({ top: 0, behavior: "smooth" });
          }}
        >
          <span className="text-white no-underline hover:font-normal px-10 flex items-center justify-center">
            {learnMoreText}
          </span>
        </Button>
      );
    }

    return <Link href={learnMoreLink} linkText={learnMoreText} />;
  })();

  return (
    <div
      className={twMerge(
        "px-8 py-12 md:py-[60px] xl:py-20 ",
        wrapperClassName,
        offsetClass
      )}
      style={{
        backgroundColor: divColor ?? "#FFFFFF",
      }}
    >
      <div className={twMerge("flex flex-col ", textWrapperClassName)}>
        <Title
          titleText={title}
          variant="h2"
          className={titleClassName}
          style={{
            color,
          }}
        />
        <Title
          titleText={subTitle}
          variant="paragraph"
          className={subTitleClassName}
          style={{
            color,
          }}
        />
        {ctaComponent}
      </div>
      <div className="flex justify-center mt-6 md:mt-8 xl:mt-[54px]">
        <ImageRenderer image={image} alt="Image" />
      </div>
    </div>
  );
};

export default ImageWrapper;
